import React, { useState, useCallback, useEffect, useRef } from 'react';
import { animated, useTransition, config } from '@react-spring/web';
import { clsx, multiply, createUseStyles } from '@pushpay/styles';
import { useScrollIntoViewOnMount } from '@pushpay/utils';
import AccordionTitle from './accordion-title';
const useStyles = createUseStyles(theme => {
  const borderStyle = `1px solid ${theme.colors['color-border-surface']}`;
  return {
    root: {
      width: '100%',
      backgroundColor: theme.colors['color-background-surface'],
      borderTop: borderStyle
    },
    titleContainer: {},
    title: {},
    activeTitle: {},
    inactiveTitle: {},
    contentContainer: {
      position: 'relative',
      overflow: 'hidden',
      borderTop: borderStyle
    },
    content: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      backgroundColor: theme.colors['color-background-page'],
      padding: multiply(theme.SPACING.GUTTER, 2),
      borderBottom: borderStyle
    },
    contentDisableAnimation: {
      position: 'unset',
      top: 'unset',
      left: 'unset'
    }
  };
});

const getHeightTransitionStyles = target => {
  if (!target) {
    return {
      leave: {
        height: '0px'
      }
    };
  }

  const {
    height
  } = target.getBoundingClientRect();
  return {
    from: {
      height: '0px'
    },
    enter: {
      height: `${height}px`
    },
    leave: {
      height: '0px'
    },
    config: {
      tension: 180,
      friction: 20,
      clamp: true
    }
  };
};

const opacityTransitionStyles = {
  from: {
    opacity: 1
  },
  leave: {
    opacity: 0
  },
  config: config.stiff
};

const emptyOnClickHandler = () => {};

const Accordion = function Accordion(props) {
  const {
    items,
    getKey,
    renderTitle,
    renderContent,
    initialExpandedItemKey,
    scrollToItemOnLoad,
    onExpand = () => {},
    isExpandable,
    showArrowOnlyOnHover = false,
    classes: classesProp,
    className: classNameProp,
    'data-pp-at-target': targetId,
    disableAnimation = false,
    inactiveItemKeys = [],
    ...rest
  } = props;
  const classes = useStyles(classesProp);
  const className = clsx(classes.root, classNameProp);
  const contents = useRef({});
  const [expandedItemKey, setExpandedItemKey] = useState(initialExpandedItemKey);
  const [heightTransitionItemKey, setHeightTransitionItemKey] = useState();
  useEffect(() => {
    // We need to get the height of the content before starting the expanding transition.
    setHeightTransitionItemKey(expandedItemKey);
    onExpand(expandedItemKey);
  }, [expandedItemKey]);
  const opacityTransitions = useTransition(expandedItemKey, opacityTransitionStyles);
  const heightTransitions = useTransition(heightTransitionItemKey, getHeightTransitionStyles(heightTransitionItemKey ? contents.current[heightTransitionItemKey] : null));
  const onClickTitle = useCallback(item => {
    setExpandedItemKey(current => {
      const key = getKey(item);

      if (current === key) {
        return undefined;
      }

      return key;
    });
  }, [setExpandedItemKey]);
  const activeItemRef = useScrollIntoViewOnMount({
    behavior: 'auto'
  });
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: className,
    "data-pp-at-target": targetId
  }, rest), items.map(item => {
    const itemKey = getKey(item);
    const itemTargetId = `${targetId}-${itemKey}`;
    const isItemExpandable = !isExpandable || isExpandable(item);
    const isItemInactive = inactiveItemKeys.findIndex(key => key === itemKey) !== -1;
    return /*#__PURE__*/React.createElement("div", {
      key: itemKey,
      "data-pp-at-target": itemTargetId,
      ref: scrollToItemOnLoad && itemKey === expandedItemKey ? activeItemRef : null,
      className: classes.titleContainer
    }, /*#__PURE__*/React.createElement(AccordionTitle, {
      className: clsx(classes.title, itemKey === expandedItemKey && classes.activeTitle, isItemInactive && classes.inactiveTitle),
      item: item,
      renderTitle: renderTitle,
      onClick: isItemExpandable ? onClickTitle : emptyOnClickHandler,
      isOpen: expandedItemKey === itemKey,
      showArrowOnlyOnHover: showArrowOnlyOnHover,
      isExpandable: isItemExpandable,
      "data-pp-at-target": `${itemTargetId}-title`
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.contentContainer
    }, disableAnimation ? expandedItemKey && itemKey === expandedItemKey && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: clsx(classes.contentDisableAnimation, classes.content),
      "data-pp-at-target": `${itemTargetId}-content`,
      ref: instance => {
        contents.current[itemKey] = instance;
      }
    }, renderContent(item))) : /*#__PURE__*/React.createElement(React.Fragment, null, opacityTransitions((styles, current) => current === itemKey && /*#__PURE__*/React.createElement(animated.div, {
      style: styles,
      ref: instance => {
        contents.current[itemKey] = instance;
      },
      className: classes.content,
      "data-pp-at-target": `${itemTargetId}-content`
    }, renderContent(item))), heightTransitions((styles, current) => current === itemKey && /*#__PURE__*/React.createElement(animated.div, {
      style: styles
    })))));
  }));
};

export default Accordion;